<template>
  <div
    class="single-select"
    :class="`variant-${props.variant}`"
    ref="selectorRef"
  >
    <div
      class="select-trigger"
      :class="[
        `variant-${props.variant}`,
        { active: isOpen, 'has-value': hasValue },
      ]"
      @click="toggleDropdown"
    >
      <div class="trigger-content">
        <span class="select-text">
          <span v-if="prefix" class="prefix-text">{{ prefix }}</span>
          <span
            v-html="displayValue"
            :class="prefix && 'prefix-text_value'"
          ></span>
        </span>
      </div>
      <svg
        v-if="props.clearable && hasValue"
        class="clear-icon"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="button"
        aria-label="Clear selection"
        @click.stop="clearValue"
      >
        <path
          d="M6 18L18 6M6 6L18 18"
          stroke="#C21D1D"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <svg
        v-else
        class="dropdown-arrow"
        :class="{ rotated: isOpen }"
        width="12"
        height="8"
        viewBox="0 0 12 8"
        fill="none"
      >
        <path
          d="M1 1.5L6 6.5L11 1.5"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>

    <Transition name="dropdown">
      <Teleport v-if="props.teleport" to="body">
        <div
          v-if="isOpen && positionReady"
          class="select-dropdown"
          :class="`variant-${props.variant}`"
          :style="teleportedDropdownStyles"
          ref="dropdownRef"
        >
          <div v-if="props.showSearch" class="search-container">
            <div class="search-input-wrapper" @click.stop>
              <SearchIcon width="20" height="20" color="#6C737F" />
              <input
                v-model="searchQuery"
                class="search-input"
                type="text"
                :placeholder="props.inputPlaceholder"
              />
            </div>
          </div>
          <div class="options-container" :style="optionsContainerStyles">
            <div
              v-if="filteredOptions.length === 0"
              class="select-option empty-state"
            >
              <span class="option-text">{{ props.emptyText }}</span>
            </div>
            <div
              v-else
              v-for="option in filteredOptions"
              :key="getOptionValue(option)"
              class="select-option"
              :class="{
                selected: isSelected(getOptionValue(option)),
                disabled: option.disabled,
              }"
              @click="selectOption(option)"
            >
              <div class="option-content">
                <span class="option-text" v-html="getOptionText(option)"></span>
                <svg
                  v-if="isSelected(getOptionValue(option))"
                  class="checkmark-icon"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="10" cy="10" r="10" fill="#003D6A" />
                  <path
                    d="M6 10L8.5 12.5L14 7"
                    stroke="white"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </Teleport>
      <div
        v-else-if="isOpen"
        class="select-dropdown"
        :class="[
          `variant-${props.variant}`,
          { 'drop-up': isDropUp, 'right-aligned': isRightAligned },
        ]"
        :style="dropdownStyles"
      >
        <div v-if="props.showSearch" class="search-container">
          <div class="search-input-wrapper" @click.stop>
            <SearchIcon width="20" height="20" color="#6C737F" />
            <input
              v-model="searchQuery"
              class="search-input"
              type="text"
              :placeholder="props.inputPlaceholder"
            />
          </div>
        </div>
        <div class="options-container" :style="optionsContainerStyles">
          <div
            v-if="filteredOptions.length === 0"
            class="select-option empty-state"
          >
            <span class="option-text">{{ props.emptyText }}</span>
          </div>
          <div
            v-else
            v-for="option in filteredOptions"
            :key="getOptionValue(option)"
            class="select-option"
            :class="{
              selected: isSelected(getOptionValue(option)),
              disabled: option.disabled,
            }"
            @click="selectOption(option)"
          >
            <div class="option-content">
              <span class="option-text" v-html="getOptionText(option)"></span>
              <svg
                v-if="isSelected(getOptionValue(option))"
                class="checkmark-icon"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="10" cy="10" r="10" fill="#003D6A" />
                <path
                  d="M6 10L8.5 12.5L14 7"
                  stroke="white"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  onUnmounted,
  watch,
  nextTick,
  defineProps,
  defineEmits,
  defineExpose,
} from "vue";
import SearchIcon from "@/components/icons/SearchIcon.vue";

const props = defineProps({
  modelValue: {
    type: String,
    default: "",
  },
  options: {
    type: Array,
    default: () => [],
  },
  placeholder: {
    type: String,
    default: "Select option",
  },
  prefix: {
    type: String,
    default: "",
  },
  maxHeight: {
    type: String,
    default: "250px",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String,
    default: "default",
    validator: (value) =>
      ["default", "dark", "form", "bordered", "purple", "pink"].includes(value),
  },
  teleport: {
    type: Boolean,
    default: false,
  },
  showSearch: {
    type: Boolean,
    default: false,
  },
  inputPlaceholder: {
    type: String,
    default: "Search...",
  },
  emptyText: {
    type: String,
    default: "No items found",
  },
  matchTriggerWidth: {
    type: Boolean,
    default: false,
  },
  clearable: {
    type: Boolean,
    default: false,
  },
  order: {
    type: String,
    default: "",
    validator: (value) => ["", "a-z", "z-a"].includes(value),
  },
});

const hasValue = computed(
  () => props.modelValue !== "" && props.modelValue != null
);

const clearValue = () => {
  emit("update:modelValue", "");
  emit("change", { value: "", option: null });
};

const emit = defineEmits(["update:modelValue", "change"]);

const selectorRef = ref(null);
const dropdownRef = ref(null);
const isOpen = ref(false);
const searchQuery = ref("");
const positionReady = ref(false);

const orderedOptions = computed(() => {
  if (!props.order) return props.options;
  return [...props.options].sort((a, b) => {
    const cmp = getOptionText(a).localeCompare(getOptionText(b));
    return props.order === "z-a" ? -cmp : cmp;
  });
});

const sortedOptions = computed(() => {
  if (!props.modelValue) return orderedOptions.value;
  const selected = [];
  const rest = [];
  for (const opt of orderedOptions.value) {
    if (getOptionValue(opt) === props.modelValue) {
      selected.push(opt);
    } else {
      rest.push(opt);
    }
  }
  return [...selected, ...rest];
});

const filteredOptions = computed(() => {
  if (!props.showSearch || !searchQuery.value) return sortedOptions.value;
  const q = searchQuery.value.toLowerCase();
  return sortedOptions.value.filter((opt) =>
    getOptionText(opt).toLowerCase().includes(q)
  );
});
const dropdownPosition = ref({ top: 0, left: 0, width: 0, bottom: 0 });
const isDropUp = ref(false);
const isRightAligned = ref(false);

const displayValue = computed(() => {
  if (!props.modelValue) return props.placeholder;

  const option = props.options.find(
    (opt) => getOptionValue(opt) === props.modelValue
  );
  return option ? getOptionText(option) : props.modelValue;
});

const dropdownStyles = computed(() => ({
  maxHeight: props.maxHeight,
}));

const teleportedDropdownStyles = computed(() => {
  const baseStyles = {
    maxHeight: props.maxHeight,
    position: "fixed",
    left: `${dropdownPosition.value.left}px`,
    width: `${dropdownPosition.value.width}px`,
    minWidth: props.matchTriggerWidth ? "0" : undefined,
    zIndex: 10000,
  };

  if (isDropUp.value) {
    baseStyles.bottom = `${dropdownPosition.value.bottom}px`;
    baseStyles.top = "auto"; // important
  } else {
    baseStyles.top = `${dropdownPosition.value.top}px`;
  }

  return baseStyles;
});

const optionsContainerStyles = computed(() => ({
  maxHeight: `calc(${props.maxHeight} - ${props.showSearch ? "66px" : "16px"})`,
}));

const updateDropdownPosition = () => {
  if (!props.teleport || !selectorRef.value || !isOpen.value) {
    positionReady.value = false;
    return;
  }

  positionReady.value = false;

  nextTick(() => {
    const trigger = selectorRef.value?.querySelector(".select-trigger");
    if (!trigger) {
      positionReady.value = false;
      return;
    }

    const rect = trigger.getBoundingClientRect();

    let top = rect.bottom + 4;
    let left = rect.left;
    let width = rect.width;

    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const maxHeight = parseInt(props.maxHeight) || 245;
    const minWidthRequired = props.matchTriggerWidth ? 0 : 360;
    const actualWidth = props.matchTriggerWidth
      ? width
      : Math.max(minWidthRequired, width);

    // Check vertical availability
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < maxHeight && spaceAbove > spaceBelow) {
      // Drop Up
      isDropUp.value = true;

      const bottom = viewportHeight - rect.top + 4;

      if (left + actualWidth > viewportWidth) {
        left = viewportWidth - actualWidth - 10;
      }

      // Update position with bottom
      dropdownPosition.value = {
        top: 0, // Ignored
        left: Math.max(10, left),
        width: actualWidth,
        bottom: bottom,
      };
    } else {
      // Drop Down (Default)
      isDropUp.value = false;

      if (left + actualWidth > viewportWidth) {
        left = viewportWidth - actualWidth - 10;
      }

      if (left < 0) {
        left = 10;
      }

      dropdownPosition.value = {
        top: Math.max(10, top),
        left: Math.max(10, left),
        width: actualWidth,
        bottom: 0, // Ignored
      };
    }

    positionReady.value = true;
  });
};

const calculatePosition = () => {
  if (!selectorRef.value) return;
  const trigger = selectorRef.value.querySelector(".select-trigger");
  if (!trigger) return;

  const rect = trigger.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const maxHeight = parseInt(props.maxHeight) || 245;
  const minWidthRequired = 360;

  // Vertical
  const spaceBelow = viewportHeight - rect.bottom;
  const spaceAbove = rect.top;
  isDropUp.value = spaceBelow < maxHeight && spaceAbove > spaceBelow;

  // Horizontal
  const spaceRight = viewportWidth - rect.left;
  isRightAligned.value = spaceRight < minWidthRequired;
};

// Helper functions to handle different option formats
const getOptionValue = (option) => {
  return option?.value !== undefined ? option.value : option;
};

const getOptionText = (option) => {
  return option?.text || option?.title || option?.label || String(option);
};

const isSelected = (value) => {
  return props.modelValue === value;
};

const toggleDropdown = () => {
  if (props.disabled) return;
  const wasOpen = isOpen.value;

  if (!wasOpen) {
    calculatePosition();
  }

  isOpen.value = !isOpen.value;

  if (isOpen.value) {
    searchQuery.value = "";
  }

  if (!wasOpen && isOpen.value && props.teleport) {
    positionReady.value = false;
    updateDropdownPosition();
  } else if (!isOpen.value) {
    positionReady.value = false;
  }
};

const selectOption = (option) => {
  if (option.disabled) return;

  const value = getOptionValue(option);
  emit("update:modelValue", value);
  emit("change", {
    value,
    option,
  });
  isOpen.value = false;
};

const handleClickOutside = (event) => {
  if (!isOpen.value) return;

  const clickedInsideSelector = selectorRef.value?.contains(event.target);
  const clickedInsideDropdown = props.teleport
    ? dropdownRef.value?.contains(event.target)
    : selectorRef.value
        ?.querySelector(".select-dropdown")
        ?.contains(event.target);

  if (!clickedInsideSelector && !clickedInsideDropdown) {
    isOpen.value = false;
    positionReady.value = false;
  }
};

const handleResize = () => {
  if (isOpen.value && props.teleport) {
    updateDropdownPosition();
  }
};

const handleScroll = (event) => {
  if (!isOpen.value || !props.teleport) return;

  const target = event.target;
  const dropdownElement = dropdownRef.value;
  if (!dropdownElement) return;

  const targetIsNode = target && typeof target.nodeType === "number";
  const isScrollingInsideDropdown =
    targetIsNode && dropdownElement.contains(target);

  if (!isScrollingInsideDropdown) {
    isOpen.value = false;
    positionReady.value = false;
  }
};

watch(isOpen, (newValue) => {
  if (newValue && props.teleport) {
    positionReady.value = false;
    updateDropdownPosition();
    window.addEventListener("resize", handleResize);
    document.addEventListener("scroll", handleScroll, true);
  } else {
    positionReady.value = false;
    window.removeEventListener("resize", handleResize);
    document.removeEventListener("scroll", handleScroll, true);
  }
});

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  window.removeEventListener("resize", handleResize);
  document.removeEventListener("scroll", handleScroll, true);
});

// Expose methods for parent components
defineExpose({
  toggleDropdown,
});
</script>

<style scoped>
.single-select {
  position: relative;
  width: fit-content;
  z-index: 1;
}

.single-select:has(.select-trigger.active) {
  z-index: 9999;
}

.single-select.variant-form {
  width: 100%;
}

.single-select.variant-bordered {
  width: 100%;
}

.select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid var(--neutral-700);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 40px;
  font-size: var(--fs-big);
}

.select-trigger.variant-form {
  padding: 4px 16px;
  height: 40px;
  border: 1px solid #6c737f;
  gap: 16px;
}

.select-trigger:hover {
  border-color: var(--blue-dark-2);
}

.select-trigger.variant-form:hover {
  border-color: #6c737f;
}

.select-trigger.active {
  border-color: var(--blue-dark-2);
  background-color: var(--blue-dark-2);
}

.select-trigger.variant-form.active {
  border-color: #6c737f;
  background-color: white;
}

.select-trigger.variant-bordered {
  height: auto;
  min-height: auto;
  border: 1px solid #212121;
  border-radius: 8px;
  gap: 0;
}

.select-trigger.variant-bordered.active {
  background-color: white;
}

.select-trigger.active .select-text,
.select-trigger.active .dropdown-arrow {
  color: #ffffff;
}

.select-trigger.variant-form.active .select-text,
.select-trigger.variant-form.active .dropdown-arrow {
  color: #3b4648;
}

.select-trigger.variant-bordered.active .select-text,
.select-trigger.variant-bordered.active .dropdown-arrow {
  color: #3b4648;
}

.trigger-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.prefix-text {
  font-weight: 400;
  color: var(--primary-blue);
  white-space: nowrap;
}

.prefix-text_value {
  color: var(--primary-blue);
}

.select-trigger.active .prefix-text,
.select-trigger.active .prefix-text_value {
  color: #ffffff;
}

/* Dark variant styles */
.select-trigger.variant-dark.active {
  background: #061f32;
  border-color: #061f32;
  color: #ffffff;
}

.select-trigger.variant-dark.active .select-text,
.select-trigger.variant-dark.active .prefix-text {
  color: #ffffff;
}

.select-text {
  font-size: var(--fs-big);
  font-weight: 400;
  color: var(--neutral-700);
  flex: 1;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.select-trigger.has-value .select-text {
  color: var(--black-body-text);
}

.select-trigger.has-value.active .select-text {
  color: #ffffff;
}

.clear-icon {
  flex-shrink: 0;
  cursor: pointer;
}

.select-trigger.active .clear-icon path {
  stroke: #ffffff;
}

.select-trigger.variant-form .select-text {
  font-weight: 500;
  font-size: var(--fs-big);
  line-height: 24px;
  color: #3b4648;
  letter-spacing: 0.024px;
}

.select-trigger.variant-bordered .select-text {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-big);
  line-height: 24px;
  color: #3b4648;
}

.dropdown-arrow {
  flex-shrink: 0;
  color: #6b7280;
  transition: transform 0.2s ease;
}

.select-trigger.variant-form .dropdown-arrow {
  color: #6c737f;
}

.select-trigger.variant-bordered .dropdown-arrow {
  color: #6c737f;
  flex-shrink: 0;
}

.dropdown-arrow.rotated {
  transform: rotate(180deg);
}

.select-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  z-index: 9999;
  min-width: 360px;
  height: auto;
  padding: 8px;
}

.select-dropdown.right-aligned {
  left: auto !important;
  right: 0;
}

.select-dropdown[style*="position: fixed"] {
  position: fixed !important;
}

.variant-form .select-dropdown {
  border: 1px solid #6c737f;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 200px;
}

.variant-bordered .select-dropdown {
  border-radius: 8px;
  box-shadow: none;
}

.search-container {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-bottom: 1px solid #e5e7eb;
}

.search-input-wrapper {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 16px;
  height: 40px;
  padding: 4px 16px;
  border: 1px solid #6c737f;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
  transition: border-color 0.2s ease;
}

.search-input-wrapper:focus-within {
  border-color: var(--primary-blue, #003d6a);
}

.search-input {
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 0;
  border: none;
  outline: none;
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-big);
  line-height: 24px;
  color: #3b4648;
  background: transparent;
}

.search-input::placeholder {
  color: #98a2b3;
}

.empty-state {
  justify-content: center;
  cursor: default !important;
  color: #3b4648 !important;
  pointer-events: none;
}

.options-container {
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
}

.options-container::-webkit-scrollbar {
  width: 6px;
}

.options-container::-webkit-scrollbar-track {
  background: #f1f5f9;
}

.options-container::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.options-container::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.variant-dark .options-container {
  padding: 10px;
}

.select-option {
  display: flex;
  align-items: center;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.variant-form .select-option {
  padding: 12px 16px;
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-big);
  line-height: 24px;
  color: #3b4648;
}

.variant-bordered .select-option {
  padding: 12px 16px;
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-big);
  line-height: 24px;
  color: #3b4648;
  border-bottom: 1px solid #e5e7eb;
  position: relative;
}

.variant-bordered .select-option:last-child {
  border-bottom: none;
}

.select-option:last-child {
  border-bottom: none;
}

.select-option:hover:not(.disabled) {
  background-color: #f9fafb;
}

.variant-form .select-option:hover:not(.disabled) {
  background-color: #f2f3fb;
}

.variant-bordered .select-option:hover:not(.disabled) {
  background-color: white;
}

.select-option.selected {
  background-color: #f2f3fb;
  color: var(--primary-blue);
  border-radius: 12px;
  padding: 10px 16px;
}

.select-option.selected:hover {
  background-color: #e8e7f5;
}

.variant-bordered .select-option.selected {
  background-color: #e91e63;
  color: #ffffff;
  border-bottom-color: #e91e63;
}

.variant-bordered .select-option.selected:hover {
  background-color: #e91e63;
  color: #ffffff;
}

.variant-bordered .select-option.selected .option-text {
  color: #ffffff;
}
/* Dark variant dropdown styles */
.variant-dark .select-dropdown {
  background: #ffffff;
  border-color: #e5e7eb;
  color: #ffffff;
}

.variant-dark .select-option.selected {
  background-color: var(--primary-blue);
  color: #ffffff;
  border-radius: 8px;
}

.variant-dark .select-option.selected:hover {
  background-color: var(--primary-blue);
  color: #ffffff;
  border-radius: 8px;
}

.select-option.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.option-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
}

.option-text {
  flex: 1;
  font-size: var(--fs-big);
  font-weight: 700;
  color: var(--primary-blue);
}

.checkmark-icon {
  flex-shrink: 0;
  margin-left: auto;
}

.variant-dark .select-option.selected .option-text {
  color: #ffffff;
}

/* Purple variant styles */
.select-trigger.variant-purple.active {
  background: var(--primary-purple);
  border-color: var(--primary-purple);
  color: #ffffff;
}

.select-trigger.variant-purple.active .select-text,
.select-trigger.variant-purple.active .prefix-text,
.select-trigger.variant-purple.active .dropdown-arrow {
  color: #ffffff;
}

.variant-purple .select-option.selected {
  background-color: #f2f3fb;
  color: var(--primary-purple);
}

.variant-purple .select-option.selected .option-text {
  color: var(--primary-purple);
}

.variant-purple .checkmark-icon circle {
  fill: var(--primary-purple);
}

/* Pink variant styles */
.select-trigger.variant-pink.active {
  background: #fd72b3;
  border-color: #fd72b3;
  color: #ffffff;
}

.select-trigger.variant-pink.active .select-text,
.select-trigger.variant-pink.active .prefix-text,
.select-trigger.variant-pink.active .dropdown-arrow {
  color: #ffffff;
}

.variant-pink .select-option.selected {
  background-color: #fce6f0;
  color: #e00069;
}

.variant-pink .select-option.selected .option-text {
  color: #e00069;
}

.variant-pink .checkmark-icon circle {
  fill: #e00069;
}

/* Dropdown animation */
.dropdown-enter-active {
  transition: all 0.2s ease;
}

.dropdown-leave-active {
  transition: all 0.15s ease;
}

.dropdown-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}

.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Position adjustments */
.select-dropdown.drop-up {
  top: auto;
  bottom: calc(100% + 4px);
  margin-bottom: 0;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .select-trigger {
    padding: 8px 10px;
    gap: 6px;
    min-height: 36px;
  }

  .select-dropdown {
    min-width: 200px;
  }

  .select-option {
    padding: 10px;
  }
}

@media (max-width: 480px) {
  .select-trigger {
    padding: 6px 8px;
    gap: 4px;
    min-height: 32px;
  }

  .select-dropdown {
    min-width: 180px;
  }
}
</style>
